import React, { useContext, useEffect, useState } from "react";
import { Redirect } from "react-router-dom";
import { CenteredTemplate, DefaultTemplate } from "templates/templates";
import { getOriginalRequestUrl } from "hooks/useAuthTokens";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { useGlobalState } from "hooks/useGlobalState";

import NotFound from './not-found';
import AppLoading from './app-loading';
import LandingPage from './landing-page';
import Dashboard from './dashboard';
import Explorer from './explorer';
import Audiences from './audiences';
import Locations from './locations';
import Uploads from './uploads';
import UploadDetails from './upload-details'
import UploadStatus from './upload-status';
import Orders from './orders';
import Models from './models';
import CreateModel from './model';
import ModelDetails from './model-details';
import ModelStatus from './model-status';
import ProcessUpload from "./process-upload";
import ProcessUploadReview from "./process-upload-review";
import PlaceOrder from './order';
import OrderDetails from './order-details';
import OrderStatus from './order-status';
import OrderFinalizeExternalDestination from '../partials/census-sync/order-finalize-external-destination';
import Profile from './profile';
import Users from "./users";
import UserForm from "./user-form";
import Companies from "./companies";
import CompanyForm from "./company-form";
import CompanyLicense from "./company-license";
import CompanyApiAccess from "./company-api-access";
import CompanyEditLicense from "./company-edit-license";
import AdminLocationTypes from "./admin-location-types";
import NoUserFound from "./no-user";
import LocationForm from "./location-form";
import LocationUploads from "./location-uploads";
import LocationUploadDetails from "./location-upload-details";
import ProcessLocationUpload from "./process-location-upload";
import AdminForm from "./admin";
import AdminInvoiceCategories from "./admin-invoice-categories";
import SessionContext from "SessionContext";
import AudienceUnsavedModal from "partials/audienceUnsavedModal";
import AdminBillingCodes from "./admin-billing-codes";
import InvoiceCategoryForm from "./invoice-category-form";
import BillingCodeForm from "./billing-code-form";
import AdminDataPermissions from "./admin-data-permissions";
import DataPermissionForm from "./data-permission-form";
import AdminRoyaltyPricing from "./admin-royalty-pricing";
import RoyaltyForm from "./royalty-form";
import RoyaltySettingsForm from './royalty-settings-form';
import RoyaltyEditForm from './royalty-edit-form';
import AdminUserRoles from "./admin-user-roles";
import UserRoleForm from "./user-role-form";
import Reports from "./reports";
import AdminAuditLogs from "./admin-audit-logs";
import AdminSystemErrorLogs from "./admin-error-logs";
import ErrorLogDetails from "./admin-error-log-details";
import InsightsPdfReport from "./reports/insights-pdf-report";
import SuppressInvoiceCategory from "./suppress-invoice-category";
import SuppressInvoiceForm from "./suppress-invoice-form";
import SuppressInvoiceNewForm from "./suppress-invoice-new-form";
import ResellerSettings from "./reseller-settings";
import CompanySettings from "./company-settings";
import ExternalDestinations from "./external-destinations";
import ExternalDestinationForm from "./external-destination-form";
import ExternalDestinationEditForm from "./external-destination-edit-form";
import CompareList from "./compare-list";
import CompareAudiences from "./compare-audiences";
import CompareReportDetails from "./compare-report-details";

// maps url /:page to the view to render
const pages = {
  // LP
  'landing-page': LandingPage,

  // Dashboard
  'dashboard': Dashboard,

  // Explorer
  'explorer': Explorer,

  // Compare
  'compare-list': CompareList,
  'compare-audiences': CompareAudiences,
  'compare-report-details': CompareReportDetails,

  // Audiences
  'audiences': Audiences,

  // Locations
  'locations': Locations,
  'location-uploads': LocationUploads,
  'location-upload-details': LocationUploadDetails,
  'location-form': LocationForm,
  'process-location-upload': ProcessLocationUpload,

  // Uploads
  // -- create
  'upload-status': UploadStatus,
  'process-upload': ProcessUpload,
  'upload-review': ProcessUploadReview,

  // -- view
  'uploads': Uploads,
  'upload-details': UploadDetails,

  // Orders
  // -- create
  'order': PlaceOrder,
  'order-status': OrderStatus,
  'order-finalize-external-destination': OrderFinalizeExternalDestination,
  // -- view
  'orders': Orders,
  'order-details': OrderDetails,

  // Models
  // -- create
  'model' : CreateModel,
  // -- view
  'models': Models,
  'model-details': ModelDetails,
  'model-status': ModelStatus,

  // Reports
  // -- view
  'reports': Reports,
  'insights-pdf-report': InsightsPdfReport,

  // Current User
  'profile': Profile,

  // Admin
  'admin': AdminForm,
  'reseller-settings': ResellerSettings,
  // -- Users
  'admin-users': Users,
  'admin-user-form': UserForm,
  'admin-user-roles' : AdminUserRoles,
  'admin-user-role-form' : UserRoleForm,
  'admin-user-logs': AdminAuditLogs,
  'admin-error-logs': AdminSystemErrorLogs,
  'admin-error-log-details': ErrorLogDetails,
  // -- Companies
  'admin-companies': Companies,
  'admin-company-form': CompanyForm,
  'admin-company-license': CompanyLicense,
  'admin-company-api-access': CompanyApiAccess,
  'admin-company-edit-license': CompanyEditLicense,
  // -- settings
  'company-settings': CompanySettings,
  'external-destinations': ExternalDestinations,
  'external-destination-form': ExternalDestinationForm,
  'external-destination-edit-form': ExternalDestinationEditForm,
  // -- Locations
  'admin-location-form': LocationForm,
  'admin-location-types': AdminLocationTypes,
  'admin-location-uploads': LocationUploads,
  'admin-process-location-upload': ProcessLocationUpload,
  // -- Invoice
  'admin-invoice-categories': AdminInvoiceCategories,
  'admin-invoice-category-form': InvoiceCategoryForm,
  'admin-suppress-invoice-category': SuppressInvoiceCategory,
  'admin-suppress-invoice-form': SuppressInvoiceForm,
  'admin-suppress-invoice-new-form': SuppressInvoiceNewForm,
  // -- Billing
  'admin-billing-codes': AdminBillingCodes,
  'admin-billing-code-form': BillingCodeForm,
  // -- Royalties
  'admin-royalty-pricing': AdminRoyaltyPricing,
  'admin-royalty-form': RoyaltyForm,
  'admin-royalty-settings-form': RoyaltySettingsForm,
  'admin-royalty-edit-form': RoyaltyEditForm,
  // -- Permissions
  'admin-data-permissions': AdminDataPermissions,
  'admin-data-permission-form': DataPermissionForm,
  // -- Exceptions
  'no-user': NoUserFound,
};

function _pageRouter({ match, location }) {
  const session = useContext(SessionContext);
  const appState = session.appState;
  const { companyHasExternalDestinations } = useGlobalState();
  const checkPerms = session.appState.userCan;

  const [loginSessionExpired, setLoginSessionExpired] = useState();
  const [pageKey, setPageKey] = useState(Date.now().toString());
  const [currentCompanyId, setCurrentCompanyId] = useState();
  const [redirectToParentPage, setRedirectToParentPage] = useState(false);

  // get requested page name, default to landing page
  const pageName = (match.params.page || "landing-page").toLowerCase();

  /* handle switching companies */
  const _locations = ["location-form"];
  const _location_uploads = ["location-upload-details", "process-location-upload", "upload-status"];
  const _uploads = ["uploads", "process-upload", "upload-review", "upload-details"];
  const _models = ["models", "model", "model-details", "model-status"];
  const _orders = ["orders", "order", "order-details", "order-status"];
  const _compare = ["compare-list", "compare-audiences", "compare-report-details"];

  // if the current company doesn't allow creation of sub-companies: hide links to reseller settings
  const company_is_reseller = appState.activeCompany.allow_sub_companies || false;
  const show_settings_link = checkPerms('settings') && (company_is_reseller || companyHasExternalDestinations);

  useEffect(() => {
    setCurrentCompanyId(appState.activeCompany.id);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    /* when switching companies, redirect user to the root page */
    if(currentCompanyId && (appState.activeCompany.id !== currentCompanyId) && !session.audienceSaved.unsavedModalIsOpen) {
      setCurrentCompanyId(appState.activeCompany.id);
      setRedirectToParentPage(true);
    }
    // eslint-disable-next-line
  }, [appState.activeCompany.id])

  useEffect(() => {
    const noModalPages = ["model", "order"];
    if(noModalPages.includes(pageName) && !session.audienceSaved.isSaved) {
      session.audienceSaved.saved(true);
    }
  // eslint-disable-next-line
  }, [session.audienceSaved])

  useEffect(() => {
    const handleTokenChange = (event) => {
      if(window.isTokenValid) {
        setLoginSessionExpired(true);
      }
    };

    // Add the event listener
    window.addEventListener('isTokenValid', handleTokenChange);

    // Cleanup the event listener on component unmount
    return () => {
      window.removeEventListener('isTokenValid', handleTokenChange);
    };
  }, []);

  const handleUnsaveContinue = () => {
    const companyChanged = appState.activeCompany.id !== currentCompanyId;
    session.audienceSaved.onClickContinueHandler();
    setCurrentCompanyId(appState.activeCompany.id);
    setRedirectToParentPage(companyChanged);
  }

  if (!appState.loaded)
    return (
      <CenteredTemplate>
        <AppLoading />
      </CenteredTemplate>
    );
    
  const originalRequestUrl = getOriginalRequestUrl();
    
  if (!session.appState.identity.user) { return; }

  // ITL: leave for now if we need to build out sections
  // const sectionName = (match.params.section || "");

  // if not logged in, show error
  if (!appState.identity && pageName !== "no-user")
    return <Redirect to="/no-user" />;

  // if logged in and on /login, redirect to landing page
  if (appState.identity && pageName === "no-user") return <Redirect to="/" />;

  if (appState.identity && pageName === "use-fixtures")
    return <Redirect to="/" />;

  if(redirectToParentPage) {
    setRedirectToParentPage(false);
    if(_locations.includes(pageName)) {
      checkPerms('location_view') ? window.location.href = '/locations' : window.location.href = '/explorer';
    } else if(_uploads.includes(pageName)) {
      checkPerms('location_create') ? window.location.href = '/uploads' : window.location.href = '/explorer';
    } else if(_location_uploads.includes(pageName)) {
      checkPerms('location_create') ? window.location.href = '/location-uploads' : window.location.href = '/explorer';
    } else if(_models.includes(pageName)) {
      checkPerms('model_view') ? window.location.href = '/models' : window.location.href = '/explorer';
    } else if(_orders.includes(pageName)) {
      checkPerms('order_view') ? window.location.href = '/orders' : window.location.href = '/explorer';
    } else if(_compare.includes(pageName)) {
      checkPerms('audience_view') ? window.location.href = '/compare-list' : window.location.href = '/explorer';
    } else if(pageName === 'audiences') {
      checkPerms('audience_view') ? window.location.href = '/audiences' : window.location.href = '/explorer';
    }
    else {
      setPageKey(Date.now().toString());
    }
  }
  
  // check user permissions for routes
  const getPermittedRoute = () => { 
    const route = pageName;
    let access = false;
    
    // Dashboard
    if (pageName === 'dashboard' && checkPerms('dashboard_view')) { access = true; }

    // Explorer
    if (pageName === 'explorer' && checkPerms('has_explorer_view')) { access = true; } 

    // Compare Audiences
    if ((pageName === 'compare-audiences' || 
      pageName === 'compare-list' ||
      pageName === 'compare-report-details'
    ) && checkPerms('audience_view')) { access = true; }

    // Audiences
    if (pageName === 'audiences' && checkPerms('audience_view')) { access = true; }

    // Locations
    // -- view
    if (pageName === 'locations' && checkPerms('location_view')) { access = true; }
    // -- create
    if ((
      pageName === 'location-uploads' ||
      pageName === 'process-location-upload' ||
      pageName === 'location-form' 
      ) &&
      checkPerms('location_create')) { access = true; }
    // -- edit
    if ((
      pageName === 'location-form' || pageName === 'location-upload-details'
    ) && checkPerms('location_edit')) { access = true; }
    
    // Uploads
    // -- view
    if ((pageName === 'uploads' ||
         pageName === 'upload-details' ||
         pageName === 'upload-status' 
    ) &&
    checkPerms('upload_view'))
    { access = true; }
    // -- create
    if ((
      pageName === 'process-upload' ||
      pageName === 'upload-review'
      ) &&
      (checkPerms('upload_create') || checkPerms('upload_edit')))
      { access = true; }
    
    // Models
    // -- view
    if ((
      pageName === 'models' ||
      pageName === 'model-details' ||
      pageName === 'model-status' ||
      pageName === 'build-model' ||
      pageName === 'model'
      ) &&
      checkPerms('model_view')) { access = true; }

    // Orders
    // -- view
    if ((
      pageName === 'orders' ||
      pageName === 'order-details'
      ) &&
      checkPerms('order_view')) { access = true; }
    // -- create
    if ((
      pageName === 'order' ||
      pageName === 'order-status' ||
      pageName === 'order-finalize-external-destination'
      ) &&
      (checkPerms('order_create') || checkPerms('order_view'))) { access = true; }
      
    // Reports
    // -- view
    if ((pageName === 'reports') && checkPerms('report_view')) { access = true; }
    if ((pageName === 'insights-pdf-report') && checkPerms('report_view')) { access = true; }

    // Admin
    if (pageName.startsWith('admin') && checkPerms('administration')) { access = true; }

    // Settings
    if (pageName === 'company-settings' && checkPerms('settings') && show_settings_link) { access = true; }
    if (show_settings_link &&
      ['external-destinations', 'external-destination-form', 'external-destination-edit-form'].includes(pageName) &&
      checkPerms('external_destinations')
    ) {
      access = true;
    }

    // Reseller
    if (pageName === 'reseller-settings' && checkPerms('settings') && show_settings_link) { access = true; }
    
    // Current User
    if (
      pageName === 'profile' ||
      pageName === 'change-password'
    ) { access = true;  }

    // Exceptions
    if (pageName === 'no-user') { access = true; }

    if (access) {
      return route;
    } else {
      return "landing-page";
    }
  };

  const handleSessionExpired = () => {
    // navigate the user to the home page
    window.location.href = "/";
  }

  const permittedRoute = getPermittedRoute();
  if (originalRequestUrl) return <Redirect to={originalRequestUrl} />;

  const PageView = pages[permittedRoute] || NotFound;
  const TemplateView =
    PageView.template ||
    (!appState.identity ? CenteredTemplate : DefaultTemplate);
  appState.site_section = PageView.site_section || pageName;

  const args = {};
  (location.hash || "")
    .slice(1)
    .split("&")
    .map((slug) => slug.split("="))
    .forEach(
      ([key, val]) => (args[decodeURIComponent(key)] = decodeURIComponent(val))
  );

  /* show page without template for printable report */
  if (pageName === 'insights-pdf-report') {
    return <InsightsPdfReport />;
  }

  return (
    <TemplateView>
      <PageView key={pageKey}
        subpage={match.params.subpage}
        args={args}
        routeState={location.state}
      />
      <AudienceUnsavedModal
        isShowing={session.audienceSaved.unsavedModalIsOpen}
        hide={() => {
          session.audienceSaved.toggleUnsavedDataModal();
        }}
        onClickContinueHandler={handleUnsaveContinue}
        className="ErrorModal__Content"
        overlayClassName="ErrorModal__Overlay"
        ariaHideApp={false}
      ></AudienceUnsavedModal>
      <Modal centered backdrop size={"lg"} isOpen={loginSessionExpired}>
          <ModalHeader>
            Session Expired
          </ModalHeader>
          <ModalBody>
            <div className="m-3">Your session has expired. Please login again.</div>
          </ModalBody>
          <ModalFooter>
            <Button className="promote" onClick={handleSessionExpired}>Go To Login Page</Button>
          </ModalFooter>
      </Modal>
    </TemplateView>
  );
}
export default _pageRouter;
